import React, { useEffect, useMemo, useState } from 'react';

import loaderAnimation from 'assets/animations/LoaderLight.json';
import { LottieAnimation } from 'components/LottieAnimation';
import { useListeningSettingsStore } from 'modules/listening/features/settings/settingsStore';
import { cn } from 'utils/cn';

let hideTimeoutId: NodeJS.Timeout;

function replaceLottieColors(animationData: object, colorMap: Record<string, [number, number, number, number]>): object {
  // Deep clone the animation data
  const cloned = JSON.parse(JSON.stringify(animationData));
  // Recursively replace color arrays
  function traverse(obj: unknown): void {
    if (Array.isArray(obj)) {
      obj.forEach(traverse);
    } else if (obj && typeof obj === 'object') {
      for (const key in obj) {
        // Type assertion for object access
        const value = (obj as Record<string, unknown>)[key];
        if (key === 'k' && Array.isArray(value) && value.length === 4 && typeof value[0] === 'number') {
          // Check if this color matches any in colorMap
          for (const [from, to] of Object.entries(colorMap)) {
            const fromArr = JSON.parse(from);
            if (JSON.stringify(value) === JSON.stringify(fromArr)) {
              (obj as Record<string, unknown>)[key] = to;
            }
          }
        } else {
          traverse(value);
        }
      }
    }
  }
  traverse(cloned);
  return cloned;
}

// Memoize color maps to prevent recreation on every render
const LIGHT_COLOR_MAP: Record<string, [number, number, number, number]> = {};
const DARK_COLOR_MAP: Record<string, [number, number, number, number]> = {
  '[0.184,0.264,0.98,1]': [0.42, 0.471, 0.988, 1], // main stroke
  '[0.941,0.945,1,1]': [0.149, 0.161, 0.251, 1] // bg line
};

const getColorMap = (darkMode: boolean): Record<string, [number, number, number, number]> => {
  return darkMode ? DARK_COLOR_MAP : LIGHT_COLOR_MAP;
};

export const LottieLoader: React.FC<{ className?: string }> = React.memo(({ className }) => {
  const darkMode = useListeningSettingsStore(s => s.darkMode);

  // Memoize animation data creation to prevent expensive operations on every render
  const animationData = useMemo(() => {
    const colorMap = getColorMap(darkMode);
    return darkMode ? replaceLottieColors(loaderAnimation, colorMap) : loaderAnimation;
  }, [darkMode]);

  return <LottieAnimation key={darkMode ? 'dark' : 'light'} className={className} animationData={animationData} loop={true} autoplay={true} />;
});

LottieLoader.displayName = 'LottieLoader';

export const PageLoader: React.FC<{ isLoading: boolean; className?: string; extraContent?: React.ReactNode }> = ({
  isLoading,
  className,
  extraContent = null
}) => {
  const [isVisible, setIsVisible] = useState(isLoading);

  useEffect(() => {
    if (!isLoading) {
      hideTimeoutId = setTimeout(() => setIsVisible(false), 300);
    } else {
      clearTimeout(hideTimeoutId);
      setIsVisible(true);
    }

    return () => clearTimeout(hideTimeoutId);
  }, [isLoading, setIsVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={cn('fixed w-screen h-screen opacity-0 z-2000', isLoading && 'opacity-100', className)}>
      <LottieLoader className="w-[84px] h-[84px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      {extraContent}
    </div>
  );
};
